import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Trash2,
  Edit3,
  Eye,
  EyeOff,
  Save,
  X,
  Loader2,
  BookOpen,
  ExternalLink,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
}

const CATEGORY_OPTIONS = [
  { value: "parents-caregivers", label: "Parents & Caregivers" },
  { value: "allied-health", label: "Allied Health Professionals" },
];

function CourseForm({ course, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    title: course?.title || "",
    subtitle: course?.subtitle || "",
    description: course?.description || "",
    category: course?.category || "parents-caregivers",
    link: course?.link || "",
    pdf_review: course?.pdf_review || "",
    tags: course?.tags?.join(", ") || "",
    published: course?.published || false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="course-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#4A6741] mb-1 block">
            Title *
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            className="w-full px-4 py-2.5 rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-sm font-light text-[#1A1C19] outline-none"
            placeholder="Course title"
            data-testid="course-title-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#4A6741] mb-1 block">
            Category *
          </label>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-sm font-light text-[#1A1C19] outline-none cursor-pointer"
            data-testid="course-category-select"
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#4A6741] mb-1 block">
          Subtitle
        </label>
        <input
          type="text"
          value={form.subtitle}
          onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
          className="w-full px-4 py-2.5 rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-sm font-light text-[#1A1C19] outline-none"
          placeholder="Short subtitle"
          data-testid="course-subtitle-input"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#4A6741] mb-1 block">
          Description
        </label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          className="w-full px-4 py-2.5 rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-sm font-light text-[#1A1C19] outline-none resize-none"
          placeholder="Course description"
          data-testid="course-description-input"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#4A6741] mb-1 block">
            Course Link URL
          </label>
          <input
            type="url"
            value={form.link}
            onChange={(e) => setForm({ ...form, link: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-sm font-light text-[#1A1C19] outline-none"
            placeholder="https://..."
            data-testid="course-link-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#4A6741] mb-1 block">
            PDF Overview URL
          </label>
          <input
            type="url"
            value={form.pdf_review}
            onChange={(e) => setForm({ ...form, pdf_review: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-sm font-light text-[#1A1C19] outline-none"
            placeholder="https://...pdf"
            data-testid="course-pdf-input"
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#4A6741] mb-1 block">
          Tags (comma separated)
        </label>
        <input
          type="text"
          value={form.tags}
          onChange={(e) => setForm({ ...form, tags: e.target.value })}
          className="w-full px-4 py-2.5 rounded-xl bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-sm font-light text-[#1A1C19] outline-none"
          placeholder="Self-Paced, Engagement, Professional"
          data-testid="course-tags-input"
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setForm({ ...form, published: !form.published })}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-all ${
            form.published
              ? "bg-[#4A6741] text-white"
              : "bg-[#F7F5F0] text-[#5C635A]"
          }`}
          data-testid="course-published-toggle"
        >
          {form.published ? <Eye size={13} /> : <EyeOff size={13} />}
          {form.published ? "Published (Live)" : "Draft (Hidden)"}
        </button>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={saving || !form.title}
          className="btn-pill text-[11px]"
          data-testid="course-save-button"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {course ? "Update Course" : "Create Course"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="btn-pill-outline text-[11px]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/courses`, { headers: getAuthHeaders() });
      setCourses(res.data.courses);
      setError("");
    } catch {
      setError("Failed to load courses");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleCreate = async (formData) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/courses`, formData, { headers: getAuthHeaders() });
      setCreating(false);
      fetchCourses();
    } catch {
      setError("Failed to create course");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (formData) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/courses/${editingId}`, formData, { headers: getAuthHeaders() });
      setEditingId(null);
      fetchCourses();
    } catch {
      setError("Failed to update course");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm("Delete this course?")) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}`, { headers: getAuthHeaders() });
      fetchCourses();
    } catch {
      setError("Failed to delete course");
    }
  };

  const handleTogglePublish = async (course) => {
    try {
      await axios.patch(
        `${API}/admin/courses/${course.id}`,
        { published: !course.published },
        { headers: getAuthHeaders() }
      );
      fetchCourses();
    } catch {
      setError("Failed to update course");
    }
  };

  return (
    <div data-testid="admin-courses-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center">
            <BookOpen size={16} className="text-[#4A6741]" />
          </div>
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-normal text-[#1A1C19]">
              Courses
            </h2>
            <p className="text-xs text-[#5C635A] font-light">
              {courses.length} total · {courses.filter((c) => c.published).length} published
            </p>
          </div>
        </div>
        {!creating && (
          <button
            onClick={() => { setCreating(true); setEditingId(null); }}
            className="btn-pill text-[10px] py-2 px-4"
            data-testid="add-course-button"
          >
            <Plus size={13} />
            Add Course
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-xs font-light">{error}</div>
      )}

      {/* Create Form */}
      {creating && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 mb-6"
        >
          <h3 className="text-sm font-medium text-[#1A1C19] mb-4">New Course</h3>
          <CourseForm onSave={handleCreate} onCancel={() => setCreating(false)} saving={saving} />
        </motion.div>
      )}

      {/* Course List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-[#4A6741]" />
        </div>
      ) : courses.length === 0 && !creating ? (
        <div className="text-center py-12">
          <BookOpen size={28} className="text-[#5C635A] opacity-20 mx-auto mb-3" />
          <p className="text-sm font-light text-[#5C635A]">No courses yet</p>
          <button
            onClick={() => setCreating(true)}
            className="text-xs text-[#4A6741] underline mt-2"
          >
            Create your first course
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {courses.map((course) => (
            <div key={course.id} data-testid={`admin-course-${course.id}`}>
              {editingId === course.id ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="glass-card p-6"
                >
                  <h3 className="text-sm font-medium text-[#1A1C19] mb-4">
                    Edit: {course.title}
                  </h3>
                  <CourseForm
                    course={course}
                    onSave={handleUpdate}
                    onCancel={() => setEditingId(null)}
                    saving={saving}
                  />
                </motion.div>
              ) : (
                <div className="bg-white rounded-2xl p-5 flex items-center gap-4 group border border-transparent hover:border-[#EAE6DF] transition-colors">
                  {/* Status indicator */}
                  <div
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      course.published ? "bg-[#10b981]" : "bg-[#5C635A]/30"
                    }`}
                    title={course.published ? "Published" : "Draft"}
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-medium text-[#1A1C19] truncate">
                        {course.title}
                      </p>
                      <span className="text-[9px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full bg-[#EAE6DF] text-[#5C635A] shrink-0">
                        {course.category === "allied-health" ? "Allied Health" : "Parents"}
                      </span>
                    </div>
                    <p className="text-xs text-[#5C635A] font-light truncate">
                      {course.subtitle || "No subtitle"}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleTogglePublish(course)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                        course.published
                          ? "bg-[#10b981]/10 text-[#10b981] hover:bg-[#10b981]/20"
                          : "bg-[#F7F5F0] text-[#5C635A] hover:bg-[#EAE6DF]"
                      }`}
                      title={course.published ? "Unpublish" : "Publish"}
                      data-testid={`toggle-publish-${course.id}`}
                    >
                      {course.published ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    {course.link && (
                      <a
                        href={course.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider font-medium bg-[#4A6741]/10 text-[#4A6741] hover:bg-[#4A6741]/20 transition-colors whitespace-nowrap"
                        title="Preview full course"
                        data-testid={`preview-course-${course.id}`}
                      >
                        <ExternalLink size={12} />
                        Preview
                      </a>
                    )}
                    <button
                      onClick={() => { setEditingId(course.id); setCreating(false); }}
                      className="w-8 h-8 rounded-lg bg-[#F7F5F0] flex items-center justify-center text-[#5C635A] hover:bg-[#EAE6DF] transition-colors"
                      data-testid={`edit-course-${course.id}`}
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(course.id)}
                      className="w-8 h-8 rounded-lg bg-[#F7F5F0] flex items-center justify-center text-[#5C635A] hover:text-red-500 transition-colors"
                      data-testid={`delete-course-${course.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
